import classNames from 'classnames';
import { FormikErrors, useFormik } from 'formik';
import { isEqual, pick } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import { TaxResidence } from '@/modules/entity/components/common/TaxInformation.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { countryOptions } from '@/modules/entity/static/countries.ts';
import { reasonOptions } from '@/modules/entity/static/no-tin-reasons.ts';
import { nonUsPersonRadioItems } from '@/modules/entity/static/non-us-person.ts';
import { Option } from '@/modules/entity/utils/option.tsx';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { RadioButton, Select, SelectButtonOption, SelectButtons, TextInputField } from 'ui';
import { PlusIcon, TrashIcon } from 'ui/icons';

const tinOptions: Option<boolean>[] = [
  { label: 'Yes', value: true },
  { label: 'No', value: false },
];

export interface JointlyTaxResidence {
  country?: string;
  is_tin?: boolean;
  tfn_or_exemption_code?: string;
  reason_if_no_tin?: string;
  tin?: string;
}

interface JointlyTaxInformationFormik {
  tax_residence?: JointlyTaxResidence[];
  is_us_person?: boolean;
  non_us_person_certification?: string;
}

const defaultTaxResidence: JointlyTaxResidence = {
  country: '',
  is_tin: false,
  tfn_or_exemption_code: '',
  reason_if_no_tin: 'no_required',
  tin: '',
};

const validationSchema = yup.object<JointlyTaxInformationFormik>({
  tax_residence: yup.array().of(
    yup.object({
      country: yup.string().trim().required('Please choose country'),
      is_tin: yup.boolean(),
      tfn_or_exemption_code: yup
        .string()
        .trim()
        .when('country', ([country], schema) => {
          if (country === 'AUS') {
            return schema.required('This field is required');
          }
          return schema.nullable();
        }),
      tin: yup
        .string()
        .trim()
        .when('is_tin', ([is_tin], schema) => {
          if (is_tin === true) {
            return schema.required('This field is required');
          }
          return schema.nullable();
        }),
      reason_if_no_tin: yup
        .string()
        .trim()
        .when('country', ([country], schema) => {
          if (country !== 'AUS') {
            return schema.required('This field is required');
          }
          return schema.nullable();
        }),
    }),
  ),
  is_us_person: yup.boolean().required('Please choose option'),
  non_us_person_certification: yup
    .string()
    .trim()
    .when(['is_us_person'], ([is_us_person], schema) => {
      if (!is_us_person) {
        return schema.required('Please choose option');
      }
      return schema.nullable();
    }),
});

const initialValues: JointlyTaxInformationFormik = {
  tax_residence: [defaultTaxResidence],
  is_us_person: false,
  non_us_person_certification: nonUsPersonRadioItems[0].value,
};

export const JointlyTaxInformation = ({ moveForward, allowSkip }: OnboardingStepProps) => {
  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const generateInitialValues = (): JointlyTaxInformationFormik => {
    return {
      ...initialValues,
      ...data?.entity.individual_entity_details?.jointly,
      tax_residence: data?.entity.individual_entity_details?.jointly?.tax_residence || [
        {
          ...initialValues.tax_residence?.[0],
          country: data?.entity?.country || initialValues.tax_residence?.[0]?.country,
        },
      ],
      is_us_person:
        data?.entity.individual_entity_details?.jointly?.is_us_person !== undefined
          ? data?.entity.individual_entity_details?.jointly?.is_us_person
          : (data?.entity?.country || data?.entity.individual_entity_details?.country) === 'USA',
      non_us_person_certification:
        data?.entity.individual_entity_details?.jointly?.non_us_person_certification !== undefined
          ? data?.entity.individual_entity_details?.jointly?.non_us_person_certification
          : '',
    };
  };

  const formik = useFormik<JointlyTaxInformationFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(),
    onSubmit: async values => {
      const entityTypeDetails = data?.entity?.individual_entity_details?.jointly || {};
      const init = pick<JointlyTaxInformationFormik, keyof JointlyTaxInformationFormik>(values, [
        'tax_residence',
        'is_us_person',
        'non_us_person_certification',
      ]);

      const current = pick<JointlyTaxInformationFormik, keyof JointlyTaxInformationFormik>(
        entityTypeDetails,
        ['tax_residence', 'is_us_person', 'non_us_person_certification'],
      );
      if (!isEqual(init, current)) {
        await updateEntity({
          ...data?.entity,
          individual_entity_details: {
            ...data?.entity.individual_entity_details,
            jointly: {
              ...data?.entity.individual_entity_details?.jointly,
              ...values,
            },
          },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleAddTaxResidence = useCallback(() => {
    formik.setFieldValue('tax_residence', [
      ...(formik.values.tax_residence || []),
      defaultTaxResidence,
    ]);
    formik.setFieldError('tax_residence', '');
  }, [formik]);

  const handleDeleteResidence = () => {
    formik.setFieldValue('tax_residence', [
      ...(formik.values.tax_residence?.slice(0, formik.values.tax_residence?.length - 1) || []),
    ]);
  };

  const handleChangeSelectInResidence = (
    field: string,
    index: number,
    value?: string | boolean,
  ) => {
    formik.setFieldValue(`tax_residence[${index}].${field}`, value);
    formik.setFieldError(`tax_residence[${index}].${field}`, '');
  };

  const handleChangeRadio = (field: string, value?: string | boolean) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  useEffect(() => {
    const isUsPerson = formik.values.tax_residence?.some(item => item.country === 'USA');
    if (isUsPerson) {
      handleChangeRadio('is_us_person', isUsPerson);
      handleChangeRadio('non_us_person_certification', '');
    }
  }, [formik.values.tax_residence]);

  return (
    <OnboardingWrapper
      title="Jointly Investor Tax Information"
      onClickContinue={formik.handleSubmit}
      onClickSkip={allowSkip ? moveForward : undefined}
      formik={formik}
      isLoading={isLoading}
    >
      <div className="w-full flex flex-col space-y-10">
        <div className="w-full flex flex-col space-y-6">
          <div className="w-full flex justify-between items-center">
            <span className="text-4 leading-[22px]">
              Jurisdictions where the company is a tax resident
            </span>
            <button
              onClick={handleAddTaxResidence}
              className="flex items-center justify-start text-blue-600"
            >
              <img className="mr-[5px]" src={PlusIcon} alt="Loading..." />
              Add new country
            </button>
          </div>
          <div className="w-full flex flex-col space-y-2">
            {formik.values.tax_residence?.map(
              ({ is_tin, country, reason_if_no_tin }, index, all) => {
                const error = formik.errors.tax_residence?.[index] as
                  | FormikErrors<TaxResidence>
                  | undefined;
                return (
                  <div
                    key={index}
                    className={classNames(
                      'w-full flex flex-col items-start justify-center space-y-4 py-6',
                      // 'rounded-lg border border-gray-900 border-opacity-16 px-4',
                    )}
                  >
                    <div className="w-full flex items-center justify-between">
                      <p className="text-[14px] leading-[22px]">
                        Country of Tax Residence {index + 1}
                      </p>
                      {index === all.length - 1 && (
                        <button onClick={handleDeleteResidence}>
                          <img src={TrashIcon} alt="Delete" />
                        </button>
                      )}
                    </div>
                    <div className="w-full flex items-start justify-between space-x-4">
                      <Select
                        isClearable
                        name="country"
                        label="country"
                        options={countryOptions}
                        value={country}
                        onChange={value =>
                          handleChangeSelectInResidence('country', index, value || undefined)
                        }
                        error={error?.country}
                      />
                      {country === 'AUS' ? (
                        <TextInputField
                          label="TFN or Exemption Code"
                          name={`tax_residence[${index}].tfn_or_exemption_code`}
                        />
                      ) : (
                        <>
                          <SelectButtons<boolean>
                            label="do you have tin?"
                            options={tinOptions}
                            selected={
                              findDefaultValue(
                                tinOptions,
                                is_tin || false,
                              ) as SelectButtonOption<boolean>
                            }
                            onChange={selected =>
                              handleChangeSelectInResidence('is_tin', index, selected)
                            }
                          />
                          {is_tin ? (
                            <TextInputField
                              name={`tax_residence[${index}].tin`}
                              label="TIN / TFN"
                            />
                          ) : (
                            <Select
                              isClearable
                              options={reasonOptions}
                              label="reason if no tin"
                              value={reason_if_no_tin}
                              onChange={value =>
                                handleChangeSelectInResidence(
                                  'reason_if_no_tin',
                                  index,
                                  value || undefined,
                                )
                              }
                              error={error?.reason_if_no_tin}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>
        <div className="w-full flex flex-col space-y-4">
          <p className="text-[14px] leading-[22px]">
            Is the Account Holder a specified U.S. person? A U.S. person includes a U.S. citizen or
            resident alien of the U.S. even if residing outside the U.S.
          </p>
          <RadioButton
            items={tinOptions}
            value={formik.values.is_us_person}
            onChange={value => {
              handleChangeRadio('is_us_person', value);
              if (value === true) {
                handleChangeRadio('non_us_person_certification', '');
              }
            }}
          />
        </div>
        {!formik.values.is_us_person && (
          <div className="w-full flex flex-col space-y-4">
            <div className="w-full flex flex-col space-y-2">
              <p className="text-4 leading-[22px]">Non U.S Person Certification</p>
              <p className="text-3 leading-5 text-gray-600">
                Select only a single category. Select a classification that matches your FATCA
                status:
              </p>
            </div>
            <RadioButton
              value={formik.values.non_us_person_certification}
              onChange={value => handleChangeRadio('non_us_person_certification', value)}
              items={nonUsPersonRadioItems}
              error={formik.errors.non_us_person_certification}
            />
          </div>
        )}
      </div>
    </OnboardingWrapper>
  );
};
